<template>
  <div v-if="customFieldListItems.length > 0 || loading" class="custom-field-filters">
    <WdsStack justify-space-between align-center>
      <WdsText secondary>
        {{ $t('Custom fields').toUpperCase() }}
      </WdsText>
      <WdsButtonGhost
        :disable="customFields.length === 0 || loading"
        secondary
        @click="addCustomFieldFilter"
      >
        <WdsStack gap-xs align-center>
          <WdsIcon name="plus" :size="16" />
          {{ $t('Create filter') }}
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>
    <WdsDivider class="tw-mt-0.5" />
    <div v-if="!loading">
      <WdsStack
        v-for="fieldFilter in customFieldFilters"
        :key="fieldFilter.key"
        class="tw-w-full"
        direction-column
        align-start
        gap-sm
      >
        <WdsText class="tw-w-full" :sub-text="customFieldLookup[fieldFilter.id].description">
          <WdsStack justify-space-between align-center class="tw-mt-2.5">
            <span>
              {{ customFieldLookup[fieldFilter.id].title }}
            </span>
            <WdsButtonIcon transparent-background sm @click="removeCustomFieldFilter(fieldFilter)">
              <WdsIcon name="trash-can" weight="thick" :size="16" />
            </WdsButtonIcon>
          </WdsStack>
        </WdsText>
        <CustomFieldSingleSelect
          v-if="fieldFilter.value.singleSelect"
          v-model="fieldFilter.value.singleSelect.value"
          :list="customFieldLookup[fieldFilter.id].customFieldOptions"
        />
        <WdsInput
          v-else-if="fieldFilter.value.freeText"
          v-model="fieldFilter.value.freeText.value"
          :placeholder="$t('Text to search for')"
        />
      </WdsStack>
    </div>
    <WdsStack v-if="addFilterOpen" class="tw-w-full" direction-column align-start gap-sm>
      <WdsText class="tw-w-full">
        <WdsStack justify-space-between align-center class="tw-mt-2.5">
          <span>
            {{ $t('Select a field to filter by') }}
          </span>
          <WdsButtonIcon transparent-background sm @click="addFilterOpen = false">
            <WdsIcon name="trash-can" weight="thick" :size="16" />
          </WdsButtonIcon>
        </WdsStack>
      </WdsText>
      <WdsSelect
        class="tw-w-full"
        :list="customFieldListItems"
        :placeholder="$t('Custom fields')"
        @update:model-value="fieldSelected($event)"
      />
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsButtonIcon,
  WdsDivider,
  WdsIcon,
  WdsInput,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import uniqBy from 'lodash/uniqBy'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  CustomFieldFilterFieldsFragment,
  CustomFieldType,
  CustomFieldTypeValueInput,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { uuid } from '@/utils/tools'
import { graphql } from '@/gql'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import CustomFieldSingleSelect from '@/components/GraphqlDataTable/filters/CustomFields/CustomFieldSingleSelect.vue'

defineProps<{ meta: FilterMetaDataProp }>()

// The key here is used for internal tracking, and the id is the custom fields id
const model = defineModel<{ value: CustomFieldTypeValueInput; key: string; id: string }[] | null>({
  default: () => null,
})

const customFieldFilters = computed(() => model.value ?? [])

graphql(`
  fragment CustomFieldFilterFields on CustomField {
    title
    id
    description
    slug
    fieldType
    appliesTo
    customFieldOptions {
      id
      value: id
      displayOrder
      label: value # used for listing purposes
    }
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query CompanyCustomFieldsQuery($companyId: ID!) {
      company(id: $companyId) {
        id
        settings {
          staffingAgencyManagesWorker
        }
        organisations(first: 10000) {
          data {
            id
            divisions(first: 1000) {
              paginatorInfo {
                count
              }
              data {
                id
              }
            }
          }
          paginatorInfo {
            count
          }
        }
        customFields(first: 1000) {
          data {
            ...CustomFieldFilterFields
          }
        }
      }
    }
  `),
  { companyId: currentAccount.globalId },
  {
    clientId: 'publicApiCustomFields',
  },
)

const getFields = (company) => company.customFields?.data
const appliesToTC = (customField) => customField.appliesTo === TypeSupportingCustomFieldValues.Job // We should update this to be smart in the future, if possible

const customFields = computed(() => {
  if (loading.value) return []
  const companyFields = getFields(result.value?.company).flat()

  // List of accounts
  return uniqBy<any>(companyFields.flat().filter(appliesToTC), (field) => field.id)
})

const customFieldLookup = computed(() =>
  // Makes an object of type id: customField to use for lookup
  customFields.value.reduce((acc, customField) => ({ ...acc, [customField.id]: customField }), {}),
)

const customFieldListItems = computed(() =>
  customFields.value.map((customField) => ({ label: customField.title, value: customField })),
)

const addFilterOpen = ref(false)
const addCustomFieldFilter = () => {
  addFilterOpen.value = true
}

const fieldSelected = ({ value }: { value: CustomFieldFilterFieldsFragment }) => {
  addFilterOpen.value = false

  const input = {
    id: value.id,
    slug: value.slug,
    value:
      value.fieldType === CustomFieldType.SingleSelect ? value.customFieldOptions?.[0]?.value : '',
  }

  const data = {
    value: {
      freeText: value.fieldType === CustomFieldType.SingleSelect ? null : input,
      singleSelect: value.fieldType === CustomFieldType.SingleSelect ? input : null,
    },
    // Used primarily as a unique identifier for this value for this custom fields since we can have many from the same custom field
    key: uuid(),
    id: value.id, // The custom fields own id primarily
  }

  if (model.value === null) {
    model.value = [data]
  } else {
    model.value.push(data)
  }
}

const removeCustomFieldFilter = (customField) => {
  if (model.value === null) return
  const index = model.value.findIndex(({ key }) => key === customField.key)
  if (index !== -1) {
    model.value.splice(index, 1)
  }
}
</script>

<style scoped lang="scss">
.custom-field-filters {
  :deep(.wds-input-wrapper) {
    width: 100%;
  }
}
</style>
